import _extends from "@babel/runtime/helpers/esm/extends";
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { TABLE_SIZES } from '../../constants';
import { TableBody } from '../TableBody/TableBody';
import { TableCell } from '../TableCell/TableCell';
import { TableHeader } from '../TableHeader/TableHeader';
import { TableHeaderCell } from '../TableHeaderCell/TableHeaderCell';
import { TableResponsive } from '../TableResponsive/TableResponsive';
import { TableRow } from '../TableRow/TableRow';
import { TableSortable } from '../TableSortable/TableSortable';
import { TableSortableHeaderCell } from '../TableSortableHeaderCell/TableSortableHeaderCell';
import { TableProvider } from './Table.context';
import { StyledTable } from './Table.styles';

/**
 * A native [`<table>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/table) element.
 *
 * @subcomponents ['Table.Row', 'Table.Sortable', 'Table.SortableHeaderCell']
 * @since 8.0.0
 */
export var Table = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  return /*#__PURE__*/React.createElement(TableProvider, {
    value: {
      alignments: props.alignments,
      size: props.size
    }
  }, /*#__PURE__*/React.createElement(StyledTable, _extends({
    ref: forwardedRef
  }, props)));
});
Table.displayName = 'Table';
Table.propTypes = {
  /**
   * Sets the horizontal alignment for each column.
   * Ex: `['left', 'left', 'center', 'right']`. You can
   * use the `align` prop on individual cells to override.
   */
  'alignments': PropTypes.arrayOf(PropTypes.string),
  /**
   * Styles the table based on one of its preset templates
   */
  'appearance': PropTypes.oneOf(['barebones', 'horizontal', 'grid', 'zebra']),
  /**
   * Be sure to provide a unique, accessible label for the table. This enables people using
   * assistive technologies to identify the purpose of the table and to
   * differentiate among multiple tables on the same page.
   */
  'aria-label': PropTypes.string.isRequired,
  /**
   * The table content
   */
  'children': PropTypes.node.isRequired,
  /**
   * Adjusts the table's density by changing the font size and spacing
   */
  'size': PropTypes.oneOf(Object.values(TABLE_SIZES))
};
Table.defaultProps = {
  alignments: [],
  size: TABLE_SIZES.md,
  appearance: 'grid'
};
Table.Body = TableBody;
Table.Cell = TableCell;
Table.Header = TableHeader;
Table.HeaderCell = TableHeaderCell;
Table.Responsive = TableResponsive;
Table.Row = TableRow;
Table.Sortable = TableSortable;
Table.SortableHeaderCell = TableSortableHeaderCell;